import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import api from '../../../services/api';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import PersonCell from '../../../components/ui/PersonCell';

export const OWNER_ROLES = ['hr', 'hiring_manager', 'it', 'admin_office', 'finance', 'legal', 'employee', 'buddy'];
export const STATUSES = ['assigned', 'in_progress', 'completed', 'hr_approval_pending', 'sla_breached', 'cancelled'];

const multiSelectFilter = (label, options, valueRender) => ({
  filter: true,
  sort: true,
  filterType: 'custom',
  filterOptions: {
    defaultOp: 'in',
    display: (filterList, onChange, index, column) => {
      const current = filterList[index] && filterList[index][0]
        ? filterList[index][0].split(',').filter(Boolean)
        : [];
      return (
        <FormControl variant="standard" fullWidth>
          <InputLabel>{label}</InputLabel>
          <Select
            multiple
            value={current}
            onChange={(e) => {
              const v = e.target.value;
              const csv = Array.isArray(v) ? v.filter(Boolean).join(',') : '';
              filterList[index] = csv ? [csv] : [];
              onChange(filterList[index], index, column);
            }}
            renderValue={(selected) => selected.map((s) => valueRender ? valueRender(s) : s).join(', ')}
          >
            {options.map((o) => (
              <MenuItem key={o} value={o}>{valueRender ? valueRender(o) : o}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    },
  },
});

const usePbTasksApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 25, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const merged = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : (lastValidQueryParams.current || { pageNumber: 1, pageSize: 25 });
    lastValidQueryParams.current = merged;
    setFetching(true);
    api.get(`/v1/preboarding-tasks?${new URLSearchParams(merged)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 25,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  const create = useCallback(async (formData) => {
    try {
      await api.post('/v1/preboarding-tasks', {
        preboardingId: formData.preboardingId,
        title: formData.title,
        ownerRole: formData.ownerRole || 'hr',
        isMandatory: !!formData.isMandatory,
        requiresHrApproval: !!formData.requiresHrApproval,
        slaDueAt: formData.slaDueAt || null,
        notesMarkdown: formData.notesMarkdown || null,
      });
      refresh();
      return true;
    } catch { return false; }
  }, [refresh]);

  const update = useCallback(async (id, formData) => {
    try {
      await api.put(`/v1/preboarding-tasks/${id}`, {
        slaDueAt: formData.slaDueAt || null,
        notesMarkdown: formData.notesMarkdown ?? null,
      });
      refresh();
      return true;
    } catch { return false; }
  }, [refresh]);

  const start = useCallback(async (id) => {
    try { await api.post(`/v1/preboarding-tasks/${id}/start`); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const complete = useCallback(async (id) => {
    try { await api.post(`/v1/preboarding-tasks/${id}/complete`, {}); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const approve = useCallback(async (id) => {
    try { await api.post(`/v1/preboarding-tasks/${id}/approve`, {}); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const reject = useCallback(async (id, note) => {
    try { await api.post(`/v1/preboarding-tasks/${id}/reject`, { note }); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const cancel = useCallback(async (id) => {
    try { await api.post(`/v1/preboarding-tasks/${id}/cancel`); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const columns = useMemo(() => {
    const ownerLabel = (s) => t(`preboardingTask.ownerRole.${s}`, { defaultValue: s });
    const statusLabel = (s) => t(`preboardingTask.status.${s}`, { defaultValue: s });

    return [
      { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
      { name: 'preboardingId', label: 'PB', options: { filter: false, sort: false, display: 'excluded' } },
      {
        name: 'personFullName', label: t('preboardingTask.personFullName'),
        options: {
          customBodyRender: (v, tableMeta) => (
            <PersonCell name={v} profilePicture={data[tableMeta.rowIndex]?.personProfilePicture} />
          ),
          filter: true, sort: true, filterType: 'textField',
          filterOptions: { defaultOp: 'like', filterKey: 'personName[like]' },
        },
      },
      {
        name: 'title', label: t('preboardingTask.title'),
        options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } },
      },
      {
        name: 'ownerRole', label: t('preboardingTask.ownerRoleLabel'),
        options: {
          ...multiSelectFilter(t('preboardingTask.ownerRoleLabel'), OWNER_ROLES, ownerLabel),
          customBodyRender: (v) => ownerLabel(v),
        },
      },
      {
        name: 'status', label: t('preboardingTask.statusLabel'),
        options: {
          ...multiSelectFilter(t('preboardingTask.statusLabel'), STATUSES, statusLabel),
          customBodyRender: (v) => statusLabel(v),
        },
      },
      {
        name: 'isMandatory', label: t('preboardingTask.isMandatory'),
        options: { filter: true, sort: true, customBodyRender: (v) => v ? t('common.yes') : t('common.no') },
      },
      dateFilterColumn('slaDueAt', t('preboardingTask.slaDueAt'), lang),
    ];
  }, [t, lang, data]);

  // Create drawer: pick an open preboarding (searched by new-hire name) and
  // describe the one-off task. Edit drawer only exposes the fields the API
  // allows on an existing task (SLA + notes).
  const createSchema = useMemo(() => ([
    {
      name: 'preboardingId', label: t('preboardingTask.form.preboarding'), type: 'asyncSelect', required: true,
      onSearch: async (q) => {
        const params = { 'status[in]': 'active,extended', pageSize: 20 };
        if (q) params['personName[like]'] = q;
        const res = await api.get('/v1/preboardings', { params });
        const r = res.data?.data; const items = Array.isArray(r) ? r : (r?.data || []);
        return items.map((p) => ({ id: p.id, name: p.personFullName || p.id }));
      },
      onGetById: async (id) => {
        const res = await api.get(`/v1/preboardings/${id}`);
        const p = res.data?.data || {};
        return { id, name: p.personFullName || id };
      },
    },
    { name: 'title', label: t('preboardingTask.form.title'), type: 'text', required: true },
    {
      name: 'ownerRole', label: t('preboardingTask.form.ownerRole'), type: 'select', grid: 6, required: true,
      options: OWNER_ROLES.map((r) => ({ id: r, name: t(`preboardingTask.ownerRole.${r}`, { defaultValue: r }) })),
    },
    { name: 'slaDueAt', label: t('preboardingTask.form.slaDueAt'), type: 'datetime', grid: 6 },
    { name: 'isMandatory', label: t('preboardingTask.form.isMandatory'), type: 'checkbox', grid: 6 },
    { name: 'requiresHrApproval', label: t('preboardingTask.form.requiresHrApproval'), type: 'checkbox', grid: 6 },
    { name: 'notesMarkdown', label: t('preboardingTask.form.notes'), type: 'textarea' },
  ]), [t]);

  const editSchema = useMemo(() => ([
    { name: 'slaDueAt', label: t('preboardingTask.form.slaDueAt'), type: 'datetime' },
    { name: 'notesMarkdown', label: t('preboardingTask.form.notes'), type: 'textarea' },
  ]), [t]);

  return {
    fetching, data, pagination, columns, createSchema, editSchema,
    refresh, create, update, start, complete, approve, reject, cancel,
  };
};

export default usePbTasksApi;
